import { inject } from "fw";

import { FeatureFlagService } from "./feature-flag";
import { OrganizationModuleService } from "./organization-module";

@inject
export class RoleService {
  constructor(
    private ffs: FeatureFlagService,
    private organizationModuleService: OrganizationModuleService
  ) { }

  permissions() {
    let coreTrackPermissions = this.getCoreTrackPermissions();
    let applicationTrackPermissions = this.getApplicationTrackPermissions();
    let applicationTrackSettingsPermissions = this.getApplicationTrackSettingsPermissions();
    let contactsTrackPermissions = this.getContactsTrackPermissions();
    let marketingPermissions = this.getMarketingPermissions();
    let eventsTrackPermissions = this.getEventsTrackPermissions();
    let settingsPermissions: { [role: string]: string } = {};
    let applicationSettingsPermissions: { [role: string]: string } = {};
    let applicationPermissions: { [role: string]: string } = {};
    let contactPermissions: { [role: string]: string } = {};
    let paymentTrackPermissions = this.getPaymentTrackPermissions();

    if (this.organizationModuleService.isEnabled) {
      return {
        settingsPermissions,
        applicationPermissions,
        contactPermissions,
        applicationSettingsPermissions,
        //  org module permissions below
        coreTrackPermissions,
        marketingPermissions,
        applicationTrackPermissions,
        applicationTrackSettingsPermissions,
        contactsTrackPermissions,
        eventsTrackPermissions,
        paymentTrackPermissions,
      };
    }

    settingsPermissions = this.getSettingsPermissions();
    applicationSettingsPermissions = this.getApplicationSettingsPermissions();
    applicationPermissions = this.getApplicationPermissions();

    if (this.organizationModuleService.hasReviewFeature) {
      applicationTrackPermissions.ManageProgramSettings = "Manage Program Properties";
    }

    contactPermissions = this.getContactPermissions();

    return {
      settingsPermissions,
      applicationPermissions,
      contactPermissions,
      applicationSettingsPermissions,
      //  org module permissions below
      coreTrackPermissions,
      marketingPermissions,
      applicationTrackPermissions,
      applicationTrackSettingsPermissions,
      contactsTrackPermissions,
      eventsTrackPermissions,
      paymentTrackPermissions
    };
  }

  private getCoreTrackPermissions() {
    let coreTrackPermissions: { [role: string]: string } = {
      ManageUsers: "Manage Users",
      InviteUsers: "Invite Users",
      ManageRoles: "Manage Roles"
    }

    if (this.ffs.isFeatureFlagEnabled("Workspaces8837")) {
      coreTrackPermissions.ManageWorkspaces = "Manage Workspaces";
    }

    if (this.ffs.isFeatureFlagEnabled("InterviewManagement")) {
      coreTrackPermissions.ManageInterviewSettings = "Manage Interview Settings";
    }

    coreTrackPermissions = {
      ...coreTrackPermissions,
      ManageTeams: "Manage Teams",
      ImpersonateUsers: "Impersonate Organization Users",
      OrganizationSettings: "Manage Organization Settings",
      ManageEmailSettings: "Manage Email Settings",
      ManageReports: "Manage Reports",
      ManageGoals: "Manage Goals",
      ManageVaults: "Manage Key Vault",
    };

    if (this.ffs.isFeatureFlagEnabled("ReorganizeExportsPermissions8441")) {
      coreTrackPermissions = {
        ...coreTrackPermissions,
        ManageExports: "Manage Exports",
        GenerateExports: "Generate Exports",
        ManagePortals: "Manage Portals"
      };
    }
    if (this.organizationModuleService.canPersonalizeDashboard) {
      coreTrackPermissions.PersonalizeDashboard = "Personalize Dashboard";
    }
    if (this.ffs.isFeatureFlagEnabled("RestrictViewToSettingsPages")) {
      coreTrackPermissions.ViewAllSettingsPages = "View All Settings Pages";
    }

    return coreTrackPermissions;
  }

  private getApplicationTrackPermissions() {
    let applicationTrackPermissions: { [role: string]: string } = {};

    if (this.organizationModuleService.hasReviewFeature) {
      applicationTrackPermissions = {
        ImpersonateApplicant: "Impersonate Applicants",
        EvaluateApplications: "Review Applications",
        AssignApplications: "Assign Applications",
        EmailApplications: "Send Email to Applications",
        EditApplicationData: "Edit Application Data",
        EditApplicationProperties: "Edit Application Properties",
        EditApplicationPhase: "Change Application Phase",
        EditApplicationTags: "Edit Application Tags",
        EditApplicationStage: "Change Application Stage",
        ViewEvaluations: "View Reviews",
        ViewEncryptedFields: "View Concealed Fields",
        CommentOnApplications: "Comment on Applications",
        CopyApplication: "Copy Applications"
      };

      // this is what the name of the permission will be (Copy/Create) once CopyApplication and ApplicationOrigin are
      // deleted..
      if (this.ffs.isFeatureFlagEnabled("ApplicationOrigin")) {
        applicationTrackPermissions.CopyApplication = "Copy/Create Applications";
      }

      applicationTrackPermissions.EditApplicationDecision = "Change Application Decision";
      applicationTrackPermissions.ManageProgramSettings = "Manage Program Properties";
    }

    return applicationTrackPermissions;
  }

  private getApplicationTrackSettingsPermissions() {
    let applicationTrackSettingsPermissions: { [role: string]: string } = {};

    if (this.organizationModuleService.hasReviewFeature) {
      applicationTrackSettingsPermissions = {
        ManagePrograms: "Manage Programs",
        ManageApplicationSettings: "Manage Application View Layout & Properties",
        ManageApplicationSegments: "Manage Application Segments",
        ManageApplicantForms: "Manage Application Forms",
        ManageEvaluationForms: "Manage Review Forms",
        ManageEvaluationPhases: "Manage Review Phases"
      };

      if (!this.ffs.isFeatureFlagEnabled("ReorganizeExportsPermissions8441")) {
        applicationTrackSettingsPermissions = {
          ...applicationTrackSettingsPermissions,
          ManageExports: "Manage Exports",
          GenerateExports: "Generate Exports",
        }

        if (this.organizationModuleService.hasApplicantPortalFeature) {
          applicationTrackSettingsPermissions.ManagePortals = "Manage Portals";
        }
      }

      if (this.organizationModuleService.hasReferencesFeature) {
        applicationTrackSettingsPermissions.ManageReferenceForms = "Manage Reference Forms";
      }
      if (this.organizationModuleService.hasWorkflowFeature) {
        applicationTrackSettingsPermissions.ManageAutomationRules = "Manage Automation Rules";
      }
      if (this.organizationModuleService.hasReviewFeature) {
        applicationTrackSettingsPermissions.ManageDecisionSettings = "Manage Decision Settings";

        if (this.ffs.isFeatureFlagEnabled("ProgramProperties")) {
          applicationTrackSettingsPermissions.ManageProgramSettings = "Manage Program Properties";
        }
      }
      if (this.ffs.isFeatureFlagEnabled("Collaboration") && this.organizationModuleService.hasCollaborationFeature) {
        applicationTrackSettingsPermissions.ManageCollaboration = "Manage Collaboration Portals & Modules";
      }

      applicationTrackSettingsPermissions.GenerateDecisionLetter = "Generate Decision Letter";
      applicationTrackSettingsPermissions.ManageDecisionLetterTemplate = "Manage Decision Letter Template";
    }

    return applicationTrackSettingsPermissions;
  }

  private getContactsTrackPermissions() {
    let contactsTrackPermissions: { [role: string]: string } = {
      EditContacts: "Edit Contacts",
      ManageContactSegments: "Manage Contact Segments",
    };

    if (this.ffs.isFeatureFlagEnabled("ContactMergePermission")) {
      contactsTrackPermissions.ManageDuplicateContacts = "Manage Duplicate Contacts";
    }

    if (this.organizationModuleService.hasContactsFeature) {
      contactsTrackPermissions.ManageInquiryForms = "Inquiry Forms";
    }

    contactsTrackPermissions.ManageContactType = "Manage Contact Type";
    contactsTrackPermissions.ManageContactCalculatedFields = "Manage Contact Calculated Fields";

    if (!this.organizationModuleService.hasReviewFeature) {
      contactsTrackPermissions.ManageExports = "Manage Exports";
    }
    if (this.ffs.isFeatureFlagEnabled("SMSConversationsInbox")) {
      contactsTrackPermissions.ManageAssignedConversations = "Manage Assigned Conversations";
      contactsTrackPermissions.ViewAllConversations = "View All Conversations";
      contactsTrackPermissions.ManageAllConversations = "Manage All Conversations";
    }

    return contactsTrackPermissions;
  }

  private getMarketingPermissions() {
    let marketingPermissions: { [role: string]: string } = {
      ManageMarketingSettings: "Manage Marketing Settings",
      ViewOutreach: "View Outreach",
      ManageOutreach: "Manage Outreach Campaigns & Tracks",
      ManageEmails: "Manage Emails",
      SendEmails: "Send Emails",
      // ManagePrintPieces: "Manage Print Pieces",
      // SendPrintPieces: "Send Print Pieces",
      ManageTextMessages: "Manage Text Messages",
      SendTextMessages: "Send Text Messages",
      ManageVoiceMessages: "Manage Voice Messages",
      SendVoiceMessages: "Send Voice Messages",
      ViewEmailTemplates: "View Email Templates",
      ManageEmailTemplates: "Manage Email Templates",
      // ViewPrintTemplates: "View Print Templates",
      // ManagePrintTemplates: "Manage Print Templates",
      // ViewPdfApproval: "View PDF Approval",
      // ManagePdfApproval: "Manage PDF Approval"
    };

    return marketingPermissions;
  }

  private getEventsTrackPermissions() {
    let eventsTrackPermissions: { [role: string]: string } = {};

    if (this.ffs.isFeatureFlagEnabled("CalendarModule")) {
      eventsTrackPermissions.ViewEvents = "View Events";
      eventsTrackPermissions.ManageEvents = "Manage Events";
      eventsTrackPermissions.ManageEventEmails = "Manage Event Emails";
      eventsTrackPermissions.ManageEventRegistrations = "Manage Event Registrations";
      eventsTrackPermissions.ManageEventTypes = "Manage Event Types";
    }

    return eventsTrackPermissions;
  }

  private getSettingsPermissions() {
    let settingsPermissions: { [role: string]: string } = {
      OrganizationSettings: "Organization",
      ManageUsers: "Users & Permissions",
      InviteUsers: "Invite Users",
      ManageReports: "Reports",
      ManageGoals: "Goals",
      ImpersonateUsers: "Impersonate Users"
    };

    return settingsPermissions;
  }

  private getApplicationSettingsPermissions() {
    let applicationSettingsPermissions: { [role: string]: string } = {};

    if (this.organizationModuleService.hasReviewFeature) {
      if (this.ffs.isFeatureFlagEnabled("Workspaces8837")) {
        applicationSettingsPermissions.ManageWorkspaces = "Manage Workspaces";
      }

      if (this.ffs.isFeatureFlagEnabled("InterviewManagement")) {
        applicationSettingsPermissions.ManageInterviewSettings = "Manage Interview Settings";
      }

      applicationSettingsPermissions = {
        ...applicationSettingsPermissions,
        ManageTeams: "Teams",
        ManagePortals: "Portals",
        ManagePrograms: "Programs",
        ManageApplicationSettings: "Application View & Properties",
        ManageApplicationSegments: "Application Segments",
        ManageApplicantForms: "Application Forms",
        ManageReferenceForms: "Reference Forms",
        ManageEvaluationForms: "Review Forms",
        ManageEvaluationPhases: "Review Phases",
        ManageEmailSettings: "Email Settings",
        ManageCollaboration: "Collaboration Settings",
        ManageAutomationRules: "Edit Automation"
      };

      if (!this.ffs.isFeatureFlagEnabled("ReorganizeExportsPermissions8441")) {
        applicationSettingsPermissions.ManageExports = "Manage Exports";
        applicationSettingsPermissions.GenerateExports = "Generate Exports";
      }
    }

    return applicationSettingsPermissions;
  }

  private getApplicationPermissions() {
    let applicationPermissions: { [role: string]: string } = {};

    if (this.organizationModuleService.hasReviewFeature) {
      applicationPermissions = {
        ViewEvaluations: "View Reviews",
        EvaluateApplications: "Review Applications",
        AssignApplications: "Assign Applications",
        EditApplicationData: "Edit Application Data",
        EditApplicationProperties: "Edit Application Properties",
        EditApplicationPhase: "Change Application Phase",
        EditApplicationStage: "Change Application Stage",
        ImpersonateApplicant: "Impersonate Applicant",
        ViewEncryptedFields: "View Concealed Fields",
        EditApplicationTags: "Edit Application Tags",
        CommentOnApplications: "Comment on Applications",
        EditApplicationDecision: "Change Application Decision"
      };
    }

    return applicationPermissions;
  }

  private getContactPermissions() {
    let contactPermissions: { [role: string]: string } = {
      EditContacts: "Edit Contacts",
      ManageContactSegments: "Contact Segments",
      ManageInquiryForms: "Inquiry Forms",
      ManageContactCalculatedFields: "Manage Contact Calculated Fields",
      ManageContactType: "Manage Contact Type"
    };

    return contactPermissions;
  }

  private getPaymentTrackPermissions() {
    let paymentTrackPermissions: { [role: string]: string } = {
      ManagePaymentProcessors: "Manage Payment Processors",
      WaivePayment: "Waive Payment"
    };

    if (this.ffs.isFeatureFlagEnabled("PaymentJournal")) {
      paymentTrackPermissions.ViewPaymentDetails = "View Payment Details";
    }

    return paymentTrackPermissions;
  }
}
